/* eslint-disable no-undef */
import React from "react";
import { connect } from "react-redux";
import { filter, find, get, isEmpty, map, size } from "lodash";
import { Switch } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import moment from "moment";
import { defaultErrorMessage, NOT_BEFORE_H } from "../../../constants";
import { getAvailableTherapistsWithTimeSlots } from "../../Shared/Helpers";
import { setAvailableTherapistsWithTimeSlots } from "../../../Actions";
import LoadingOverlay from "../../Shared/LoadingOverlay";
import { updateCart } from "../Shared/helpers";
import { decode } from "../Shared/encode";
import { seBookingSchedule } from "../../Shared/WebAnalytics";
import { STEPS, formatProsWithSlotsList } from "../Shared/constants";
import { InfoOutlined } from "@material-ui/icons";
import { Close } from "@material-ui/icons";
import RangeSlider from "../../Shared/RangeSlider";
import HorizontalCalendarMonthUnderDay from "../../Shared/HorizontalCalendarMonthUnderDay";
import HourPicker from "./HourPicker";
import LatestModalDesign from "../../Shared/LatestModalDesign";
import CTAButton from "../../Shared/CTAButton";

// iOS-style Switch to match Figma
const IOSSwitch = withStyles((theme) => ({
  root: {
    width: 42,
    height: 26,
    padding: 0,
    display: "inline-flex",
    alignItems: "center",
  },
  switchBase: {
    padding: 1,
    "&$checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + $track": {
        backgroundColor: "#586B94",
        opacity: 1,
        border: "none",
      },
    },
    "&$focusVisible $thumb": {
      color: "#586B94",
      border: "6px solid #fff",
    },
  },
  thumb: {
    width: 24,
    height: 24,
    boxShadow: "none",
  },
  track: {
    borderRadius: 26 / 2,
    border: "none",
    backgroundColor: "#E0E0E0",
    opacity: 1,
  },
  checked: {},
  focusVisible: {},
}))(({ classes, ...props }) => (
  <Switch
    focusVisibleClassName={classes.focusVisible}
    disableRipple
    classes={{
      root: classes.root,
      switchBase: classes.switchBase,
      thumb: classes.thumb,
      track: classes.track,
      checked: classes.checked,
    }}
    {...props}
  />
));

class DateTimeMarketpLace extends React.Component {
  constructor(props) {
    super(props);
    
    // Use cart's session date if available, otherwise default to today
    const cartSessionDate = get(props, "booking.cart.date.utc", "");
    const defaultSessionDate = cartSessionDate 
      ? moment(cartSessionDate, "YYYY-MM-DD") 
      : moment();

    // Check if the default date is today
    const isToday = defaultSessionDate.isSame(moment(), "day");
    
    // Calculate default preferred time respecting NOT_BEFORE_H if today
    let defaultPreferredMinutes = 9 * 60; // 9:00 AM = 540 minutes as fallback
    let defaultSessionTime = "9:00 AM";
    
    if (isToday) {
      // Calculate soonest allowed time using NOT_BEFORE_H constant
      const soonestTime = moment().add(NOT_BEFORE_H, "hours");
      const maxTime = moment().hours(22).minutes(0); // 10:00 PM
      
      // If soonest time is after max time, use max time
      const selectedTime = soonestTime.isAfter(maxTime) 
        ? maxTime 
        : soonestTime;
      
      // Round to nearest 15-minute interval
      const roundedMinutes = Math.ceil(selectedTime.minutes() / 15) * 15;
      selectedTime.minutes(roundedMinutes).seconds(0);
      
      defaultSessionTime = selectedTime.format("h:mm A");
      defaultPreferredMinutes = selectedTime.hours() * 60 + selectedTime.minutes();
    }

    // Use cart's preferred time if available, otherwise use calculated default
    const cartPreferredTime = get(props, "booking.cart.info_fields.preferred_time", "");
    const finalSessionTime = cartPreferredTime ? cartPreferredTime : defaultSessionTime;
    
    // Calculate preferred minutes from the session time
    const preferredTimeMoment = moment(finalSessionTime, "h:mm A");
    const calculatedPreferredMinutes = preferredTimeMoment.hours() * 60 + preferredTimeMoment.minutes();
    const finalPreferredMinutes = isNaN(calculatedPreferredMinutes) ? defaultPreferredMinutes : calculatedPreferredMinutes;
    
    // Calculate minimum allowed time for today if applicable
    const minAllowedMinutes = isToday 
      ? moment().add(NOT_BEFORE_H, "hours").hours() * 60 + moment().add(NOT_BEFORE_H, "hours").minutes()
      : 480; // 8:00 AM for non-today dates
    
    // Calculate range based on preferred time, respecting NOT_BEFORE_H
    const calculatedRangeStart = Math.max(minAllowedMinutes, finalPreferredMinutes - 120);
    const calculatedRangeEnd = Math.min(1320, finalPreferredMinutes + 120);

    // Use cart's backup_ranges_enabled defaults to true
    const cartBackupRangesEnabled = get(props, "booking.cart.info_fields.backup_ranges_enabled");
    const backupRangesEnabled = get(props, "booking.cart.time.display", "")
      ? cartBackupRangesEnabled
      : true;

    // Check for saved range values from cart
    const cartRangeStart = get(props, "booking.cart.info_fields.range_start_by", "");
    const cartRangeEnd = get(props, "booking.cart.info_fields.range_end_by", "");

    // Convert saved range times to minutes if they exist
    let savedRangeStart = null;
    let savedRangeEnd = null;
    if (cartRangeStart) {
      const startMoment = moment(cartRangeStart, "h:mm A");
      if (startMoment.isValid()) {
        savedRangeStart = startMoment.hours() * 60 + startMoment.minutes();
      }
    }
    if (cartRangeEnd) {
      const endMoment = moment(cartRangeEnd, "h:mm A");
      if (endMoment.isValid()) {
        savedRangeEnd = endMoment.hours() * 60 + endMoment.minutes();
      }
    }

    // Use saved range if available and valid, otherwise calculate from preferred time
    const finalRangeStart = (savedRangeStart !== null && savedRangeEnd !== null)
      ? savedRangeStart
      : calculatedRangeStart;
    const finalRangeEnd = (savedRangeStart !== null && savedRangeEnd !== null)
      ? savedRangeEnd
      : calculatedRangeEnd;

    this.state = {
      sessionDate: defaultSessionDate,
      sessionTime: finalSessionTime,
      width: window.innerWidth,
      loading: false,
      formattedList: [],
      showHeadsUp: false,
      timeRangeMinutes: [finalRangeStart, finalRangeEnd],
      minMinutes: 480,
      maxMinutes: 1320,
      sliderMarks: [],
      isFlexible: backupRangesEnabled,
      showInfoModal: false,
      preferredTimeMinutes: finalPreferredMinutes,
      hasError1: false, // Error exists: preferred time must be within range
      hasError2: false, // Error exists: range should give enough time for service
      showError1Banner: true, // Whether error 1 banner is visible
      showError2Banner: true, // Whether error 2 banner is visible
      hasAvailableSlots: false, // Whether there are available slots in current range
    };
    this.listOptions = this.listOptions.bind(this);
    this.getProsOfSelectedDay = this.getProsOfSelectedDay.bind(this);
    this.formatProsList = this.formatProsList.bind(this);
    this.submitSelection = this.submitSelection.bind(this);
    this.sameDayCopy = this.sameDayCopy.bind(this);
    this.updatePreferredTime = this.updatePreferredTime.bind(this);
    this.validateTimeRange = this.validateTimeRange.bind(this);
    this.handleRangeChange = this.handleRangeChange.bind(this);
    this.renderErrorBanners = this.renderErrorBanners.bind(this);
    this.calculateAvailableSlots = this.calculateAvailableSlots.bind(this);
  }

  componentDidMount() {
    window.addEventListener("resize", () =>
      this.setState({ width: window.innerWidth })
    );

    this.getProsOfSelectedDay();
    this.sameDayCopy();
  }

  componentDidUpdate(prevProps) {
    // Check if preferred_time has changed in the cart
    const currentPreferredTime = get(this.props, "booking.cart.info_fields.preferred_time", "");
    const prevPreferredTime = get(prevProps, "booking.cart.info_fields.preferred_time", "");

    if (currentPreferredTime && currentPreferredTime !== prevPreferredTime && currentPreferredTime !== this.state.sessionTime) {
      this.updatePreferredTime(currentPreferredTime);
    }

    // Check if range values have changed in the cart
    const currentRangeStart = get(this.props, "booking.cart.info_fields.range_start_by", "");
    const currentRangeEnd = get(this.props, "booking.cart.info_fields.range_end_by", "");
    const prevRangeStart = get(prevProps, "booking.cart.info_fields.range_start_by", "");
    const prevRangeEnd = get(prevProps, "booking.cart.info_fields.range_end_by", "");

    if ((currentRangeStart !== prevRangeStart || currentRangeEnd !== prevRangeEnd) &&
        (currentRangeStart || currentRangeEnd)) {
      // Convert to minutes and update range
      let newRangeStart = null;
      let newRangeEnd = null;

      if (currentRangeStart) {
        const startMoment = moment(currentRangeStart, "h:mm A");
        if (startMoment.isValid()) {
          newRangeStart = startMoment.hours() * 60 + startMoment.minutes();
        }
      }

      if (currentRangeEnd) {
        const endMoment = moment(currentRangeEnd, "h:mm A");
        if (endMoment.isValid()) {
          newRangeEnd = endMoment.hours() * 60 + endMoment.minutes();
        }
      }

      if (newRangeStart !== null && newRangeEnd !== null) {
        this.setState({ timeRangeMinutes: [newRangeStart, newRangeEnd] }, () => {
          this.validateTimeRange(newRangeStart, newRangeEnd, this.state.preferredTimeMinutes);
          this.calculateAvailableSlots();
        });
      }
    }
  }


  sameDayCopy() {
    this.state.sessionDate && this.state.sessionDate.isSame(moment(), "day")
      ? this.setState({ showHeadsUp: true })
      : this.setState({ showHeadsUp: false });
  }

  updatePreferredTime(newTime) {
    const preferredTime = moment(newTime, "h:mm A");
    const preferredMinutes =
      preferredTime.hours() * 60 + preferredTime.minutes();

    // Check if current date is today to apply NOT_BEFORE_H constraint
    const isToday = this.state.sessionDate && this.state.sessionDate.isSame(moment(), "day");
    const minAllowedMinutes = isToday 
      ? moment().add(NOT_BEFORE_H, "hours").hours() * 60 + moment().add(NOT_BEFORE_H, "hours").minutes()
      : 480; // 8:00 AM for non-today dates

    // Update range to be ±2 hours from preferred time, clamped to available hours and max 10pm (11pm is visual only)
    const newRangeStart = Math.max(Math.max(minAllowedMinutes, this.state.minMinutes), preferredMinutes - 120);
    const newRangeEnd = Math.min(1320, preferredMinutes + 120);

    this.setState(
      {
        sessionTime: newTime,
        preferredTimeMinutes: preferredMinutes,
        timeRangeMinutes: [newRangeStart, newRangeEnd],
      },
      () => {
        this.validateTimeRange(newRangeStart, newRangeEnd, preferredMinutes);
        this.calculateAvailableSlots();
      }
    );
  }

  validateTimeRange(rangeStart, rangeEnd, preferredMinutes) {
    const serviceLength = get(
      this.props,
      "booking.cart.cartProducts.0.base_session_length",
      60
    );

    // Error 1: Preferred time must be within range
    const error1 = preferredMinutes < rangeStart || preferredMinutes > rangeEnd;

    // Error 2: Range should give enough time to complete the service from preferred time to end
    const durationFromPreferredToEnd = rangeEnd - preferredMinutes;
    const error2 = durationFromPreferredToEnd < serviceLength;

    this.setState({
      hasError1: error1,
      hasError2: error2,
      // Show banners when errors appear, but keep them hidden if user dismissed them
      showError1Banner: error1 ? true : this.state.showError1Banner,
      showError2Banner: error2 ? true : this.state.showError2Banner,
    });
  }

  handleRangeChange(e, newRange) {
    // Check if current date is today to apply NOT_BEFORE_H constraint
    const isToday = this.state.sessionDate && this.state.sessionDate.isSame(moment(), "day");
    const minAllowedMinutes = isToday 
      ? moment().add(NOT_BEFORE_H, "hours").hours() * 60 + moment().add(NOT_BEFORE_H, "hours").minutes()
      : 480; // 8:00 AM for non-today dates

    // Clamp the range to respect available times (minMinutes for today) and max 10pm (11pm is visual margin only)
    const clampedRange = [
      Math.max(Math.max(minAllowedMinutes, this.state.minMinutes), newRange[0]),
      Math.min(1320, newRange[1])
    ];
    
    this.setState({ timeRangeMinutes: clampedRange }, () => {
      this.validateTimeRange(
        clampedRange[0],
        clampedRange[1],
        this.state.preferredTimeMinutes
      );
      this.calculateAvailableSlots();
    });
  }

  calculateAvailableSlots() {
    const frmtdList = this.state.formattedList;
    
    if (isEmpty(frmtdList)) {
      this.setState({ hasAvailableSlots: false });
      return;
    }

    const hourToMinutes = (hr) => {
      const m = moment(hr, "HH:mm");
      return m.hours() * 60 + m.minutes();
    };

    // Determine filter based on flexibility
    const isInRange = (hr) => {
      const mins = hourToMinutes(hr);

      if (this.state.isFlexible) {
        // If flexible, use the time range slider
        const [fromMin, toMin] = this.state.timeRangeMinutes || [];
        return mins >= fromMin && mins <= toMin;
      } else {
        // If not flexible, show times within 2 hours of preferred start time
        const preferredTime = moment(this.state.sessionTime, "h:mm A");
        const preferredMinutes =
          preferredTime.hours() * 60 + preferredTime.minutes();
        const timeDiff = Math.abs(mins - preferredMinutes);
        return timeDiff <= 120; // Within 2 hours (120 minutes)
      }
    };

    // Calculate total available providers across all time slots in range
    const gaAllowed = get(
      this.props,
      "booking.cart.info_fields.ga_pap_enabled",
      false
    );

    let hasAvailableSlots = false;

    Object.keys(frmtdList).forEach((hr) => {
      if (isInRange(hr)) {
        const proListt = frmtdList[hr];
        const nonGAPros = filter(proListt, (pro) => !pro.isGA);
        const sz = size(gaAllowed ? proListt : nonGAPros);
        if (sz > 0) {
          hasAvailableSlots = true;
        }
      }
    });

    this.setState({ hasAvailableSlots });
  }

  renderErrorBanners() {
    return (
      <div
        style={{
          position: "relative",
          zIndex: 10,
        }}
      >
        {this.state.hasError1 && this.state.showError1Banner && (
          <div
            style={{
              backgroundColor: "#FFC043",
              borderRadius: "4px",
              padding: "16px",
              display: "flex",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                fontFamily: "Poppins",
                fontWeight: 400,
                fontSize: "16px",
                lineHeight: "1.5em",
                color: "#000000",
                flex: 1,
              }}
            >
              Your preferred time must be within the time range.
            </div>
            <div
              style={{
                cursor: "pointer",
              }}
              onClick={() => this.setState({ showError1Banner: false })}
            >
              <Close style={{ fontSize: "16px", color: "#000000" }} />
            </div>
          </div>
        )}

        {this.state.hasError2 && this.state.showError2Banner && (
          <div
            style={{
              backgroundColor: "#FFC043",
              borderRadius: "4px",
              padding: "16px",
              display: "flex",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                fontFamily: "Poppins",
                fontWeight: 400,
                fontSize: "16px",
                lineHeight: "1.5em",
                color: "#000000",
                flex: 1,
              }}
            >
              Your range should give enough time to complete the service.
            </div>
            <div
              style={{
                cursor: "pointer",
              }}
              onClick={() => this.setState({ showError2Banner: false })}
            >
              <Close style={{ fontSize: "16px", color: "#000000" }} />
            </div>
          </div>
        )}
      </div>
    );
  }

  getProsOfSelectedDay() {
    this.sameDayCopy();
    // pap marketplace not allowed for couples
    const cartId = get(this.props, "booking.cart.id", "");
    const cartProducts = get(this.props, "booking.cart.cartProducts", []);
    this.setState({ loading: true });
    getAvailableTherapistsWithTimeSlots(
      cartId,
      get(cartProducts, "0.id", ""),
      this.state.sessionDate.format("YYYY-MM-DD"),
      (resp) => {
        const providers = get(resp, "data.therapists", []);
        this.formatProsList(providers);
        this.setState({ loading: false });
      },
      (err) => {
        this.setState({ loading: false });
        this.formatProsList([]);
        this.props.displayError(
          get(err, "response.data.errors.0.message", defaultErrorMessage)
        );
      }
    );
  }

  formatProsList(therpList) {
    // Pass the appointment timezone to ensure correct time filtering
    const timezone = get(this.props, "booking.cart.time.timezone", "");
    const formatted = formatProsWithSlotsList(
      therpList,
      this.state.sessionDate.isSame(moment(), "day"),
      timezone
    );
    // derive slider bounds from available hours
    const hours = Object.keys(formatted || {});
    if (!isEmpty(hours)) {
      const toMinutes = (h) => {
        const m = moment(h, "HH:mm");
        return m.hours() * 60 + m.minutes();
      };
      const minutesList = map(hours, (h) => toMinutes(h));
      const minMinutes = Math.min.apply(null, minutesList);
      const maxMinutes = Math.max.apply(null, minutesList);
      // generate hourly marks within bounds
      const startHour = Math.floor(minMinutes / 60);
      const endHour = Math.ceil(maxMinutes / 60);
      const marks = [];
      for (let hr = startHour; hr <= endHour; hr += 2) {
        // every 2 hours to avoid clutter
        const val = hr * 60;
        marks.push({
          value: val,
          label: moment({ hour: hr, minute: 0 }).format("h a"),
        });
      }
      // Initialize the time range to ±2 hours around the preferred time, clamped to available hours and max 10pm (11pm is visual only)
      const preferredMinutes = this.state.preferredTimeMinutes;
      
      // Check if current date is today to apply NOT_BEFORE_H constraint
      const isToday = this.state.sessionDate && this.state.sessionDate.isSame(moment(), "day");
      const minAllowedMinutes = isToday 
        ? moment().add(NOT_BEFORE_H, "hours").hours() * 60 + moment().add(NOT_BEFORE_H, "hours").minutes()
        : 480; // 8:00 AM for non-today dates
      
      const clampedStart = Math.max(Math.max(minAllowedMinutes, minMinutes), preferredMinutes - 120);
      const clampedEnd = Math.min(1320, preferredMinutes + 120);

      this.setState({
        formattedList: formatted,
        minMinutes,
        maxMinutes,
        timeRangeMinutes: [clampedStart, clampedEnd],
        sliderMarks: marks,
      }, () => {
        this.validateTimeRange(clampedStart, clampedEnd, preferredMinutes);
        this.calculateAvailableSlots();
      });
      return;
    }
    this.setState({ formattedList: formatted }, () => {
      this.calculateAvailableSlots();
    });
  }

  submitSelection(hour, proList = []) {
    const backup_ranges_enabled = this.state.isFlexible;
    const cartId = get(this.props, "booking.cart.id", "");
    const startBy = backup_ranges_enabled ? get(this.state, "timeRangeMinutes.0", "") : "";
    const endBy = backup_ranges_enabled ? get(this.state, "timeRangeMinutes.1", "") : "";
    
    // Find matching backup_range_id if preferred_time matches a backup_range start_time
    const backupRanges = get(this.props, "booking.cart.info_fields.backup_ranges", []);
    const matchingRange = find(backupRanges, (range) => 
      range.start_time === this.state.sessionTime
    );
    const selectedBackupRangeId = matchingRange 
      ? matchingRange.backup_range_id 
      : get(this.props, "booking.cart.info_fields.selected_backup_range_id", "");
    
    const cartPayload = {
      session_date: moment(this.state.sessionDate).format("YYYY/MM/DD"),
      session_time: hour,
      remove_pick_a_pro: true,
      backup_ranges_enabled,
      range_start_by: startBy
        ? moment({
            hour: Math.floor(startBy / 60),
            minute: startBy % 60,
          }).format("h:mm A")
        : "",
      range_end_by: endBy
        ? moment({
            hour: Math.floor(endBy / 60),
            minute: endBy % 60,
          }).format("h:mm A")
        : "",
      preferred_time: this.state.sessionTime,
      // option_id: get(this.props, "booking.cart.cartProducts.0.id", ""),
      // for updates only
      // selected_backup_range_id: selectedBackupRangeId,
    };
    this.setState({ loading: true });
    updateCart(
      cartId,
      cartPayload,
      get(this.props, "fieldsHolder.csrfToken", ""),
      (response) => {
        this.setState({ loading: false });
        this.props.setAvailableTherapistsWithTimeSlots(proList);
        const { result } = response.data;
        if (result) {
          this.props.assignToCart({ cart: decode(response.data.cart) });

          const cart = get(this.props, "booking.cart", null);
          const productTitle = get(
            this.props,
            "booking.product.title",
            ""
          ).toLowerCase();
          const isFirstTimeBooker = get(
            this.props,
            "client.first_time_booker",
            false
          );

          seBookingSchedule(
            cart,
            productTitle,
            isFirstTimeBooker,
            "booking_23_variant",
            size(proList),
            "jul_2024_booking_enhancements",
            get(this.props, "jul_2024_booking_enhancements", "")
          );

          let nextStepKey = "REVIEW";
          if (
            get(
              this.props,
              "booking.cart.info_fields.marketplace_enabled",
              false
            ) &&
            !isEmpty(proList)
          ) {
            nextStepKey = "PICKAPRO";
          }
          const nextStepCustom = find(STEPS, (step) => step.id === nextStepKey);
          this.props.setBookingFlowStepThroughParent(nextStepCustom);
        } else {
          const message = get(
            response,
            "data.errors.0.message",
            defaultErrorMessage
          );
          this.setState({ showErrorModal: true, message });
        }
      },
      (err) => {
        this.setState({ loading: false });
        this.props.setAvailableTherapistsWithTimeSlots([]);
        this.props.displayError(
          get(err, "response.data.errors.0.message", defaultErrorMessage)
        );
      }
    );
  }

  responiveDaySelector() {
    return (
      <div className="top-sticky-80 z-index-1">
        <div className="bg-primary top-br-rd-16 p-16">
          <HorizontalCalendarMonthUnderDay
            daysCount={60}
            startDate={moment().format("YYYY-MM-DDTHH:mm:ss.SSSZ")}
            defaultSelectedDate={this.state.sessionDate}
            additionalAction={(dateEl) => {
              // Check if selected date is today
              const isToday = dateEl.isSame(moment(), "day");
              
              if (isToday) {
                // Calculate soonest allowed time using NOT_BEFORE_H constant
                const soonestTime = moment().add(NOT_BEFORE_H, "hours");
                const maxTime = moment().hours(22).minutes(0); // 10:00 PM
                
                // If soonest time is after max time, use max time
                const selectedTime = soonestTime.isAfter(maxTime) 
                  ? maxTime 
                  : soonestTime;
                
                // Round to nearest 15-minute interval
                const roundedMinutes = Math.ceil(selectedTime.minutes() / 15) * 15;
                selectedTime.minutes(roundedMinutes).seconds(0);
                
                const formattedTime = selectedTime.format("h:mm A");
                
                // Update state with new date and time
                this.setState({ sessionDate: dateEl }, () => {
                  this.updatePreferredTime(formattedTime);
                  this.getProsOfSelectedDay();
                });
              } else {
                // For other days, just update the date
                this.setState({ sessionDate: dateEl }, this.getProsOfSelectedDay);
              }
            }}
          />
        </div>
      </div>
    );
  }

  listOptions() {
    const frmtdList = this.state.formattedList;

    if (this.state.loading) {
      return <LoadingOverlay showLoader={this.state.loading} />;
    }
    if (isEmpty(frmtdList)) {
      return (
        <div className="ptb-24 txt-center">
          <div className="mb-32">
            <span
              style={{
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: "16px",
                lineHeight: "20px",
                color: "#000000",
                textAlign: "center",
                marginBottom: "8px",
                backgroundColor: "#FFFAF0",
                border: "1px solid #BC8B2C",
                borderRadius: "4px",
                padding: "16px",
              }}
            >
              0 Available Providers
            </span>
          </div>
          <div>
            <div
              style={{
                fontFamily: "Poppins",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "20px",
                color: "#BC8B2C",
                textAlign: "center",
                marginBottom: "8px",
              }}
            >
              No providers available at this time
            </div>
            <div
              style={{
                fontFamily: "Poppins",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "20px",
                color: "#BC8B2C",
                textAlign: "center",
              }}
            >
              Try a different time or adding additional availability
            </div>
          </div>
        </div>
      );
    }

    const hourToMinutes = (hr) => {
      const m = moment(hr, "HH:mm");
      return m.hours() * 60 + m.minutes();
    };

    // Determine filter based on flexibility
    const isInRange = (hr) => {
      const mins = hourToMinutes(hr);

      if (this.state.isFlexible) {
        // If flexible, use the time range slider
        const [fromMin, toMin] = this.state.timeRangeMinutes || [];
        return mins >= fromMin && mins <= toMin;
      } else {
        // If not flexible, show times within 2 hours of preferred start time
        const preferredTime = moment(this.state.sessionTime, "h:mm A");
        const preferredMinutes =
          preferredTime.hours() * 60 + preferredTime.minutes();
        const timeDiff = Math.abs(mins - preferredMinutes);
        return timeDiff <= 120; // Within 2 hours (120 minutes)
      }
    };

    // Calculate total available providers across all time slots in range
    const gaAllowed = get(
      this.props,
      "booking.cart.info_fields.ga_pap_enabled",
      false
    );

    let totalProviders = 0;
    let hasAvailableSlots = false;

    Object.keys(frmtdList).forEach((hr) => {
      if (isInRange(hr)) {
        const proListt = frmtdList[hr];
        const nonGAPros = filter(proListt, (pro) => !pro.isGA);
        const sz = size(gaAllowed ? proListt : nonGAPros);
        if (sz > 0) {
          hasAvailableSlots = true;
          totalProviders = Math.max(totalProviders, sz);
        }
      }
    });

    return (
      <>
        {/* Single Available Providers Banner */}
        {hasAvailableSlots && (
          <div className="ptb-16">
            <div className="d-flex-only justify-content-center align-items-center">
              <div
                className="d-inline-flex align-items-center justify-content-center"
                style={{
                  padding: "16px",
                  backgroundColor: "#F1F6FF",
                  border: "1px solid #7089BE",
                  borderRadius: "4px",
                }}
              >
                <span
                  style={{
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "20px",
                    color: "#39455E",
                  }}
                >
                  {totalProviders} Available Provider
                  {totalProviders !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Show message if no providers available in range */}
        {!hasAvailableSlots && (
          <div className="ptb-16">
            <div className="d-flex-only justify-content-center align-items-center mb-16">
              <div className="d-inline-flex align-items-center justify-content-center">
                <span
                  style={{
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "20px",
                    color: "#000000",
                    padding: "16px",
                    backgroundColor: "#FFFAF0",
                    border: "1px solid #BC8B2C",
                    borderRadius: "4px",
                  }}
                >
                  0 Available Providers
                </span>
              </div>
            </div>
            <div
              style={{
                fontFamily: "Poppins",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "20px",
                color: "#BC8B2C",
                textAlign: "center",
              }}
            >
              No Providers are available at this time.
              <br />
              Try a different time or adding additional availability.
            </div>
          </div>
        )}
      </>
    );
  }
  hourSelectionSection() {
    const todayMinTime = moment().add(NOT_BEFORE_H, "hours");
    const todayMaxTime = moment().hours(22).minutes(0);

    return (
      <div className="p-24-16 mb-24">
        {/* Preferred Start Time */}
        <div className="mb-24" style={{ position: "relative", zIndex: 10 }}>
          <HourPicker
            rangeSupported={true}
            date={this.state.sessionDate.format("YYYY-MM-DD")}
            time={this.state.sessionTime}
            minTime={todayMinTime.format("h:mm A")}
            maxTime={todayMaxTime.format("h:mm A")}
            onChange={this.updatePreferredTime}
          />
        </div>

        {/* I'm Flexible Toggle */}
        <div className="mb-48 d-flex-only align-items-center justify-content-spaced">
          <div className="d-flex-only align-items-center">
            <div className="size-16-20 contentPrimary font-weight-bold">
              I'm flexible with timing
            </div>
            <InfoOutlined
              className="contentSecondary cursor-pointer w-h-20"
              onClick={() => {
                this.setState({ showInfoModal: true });
              }}
            />
          </div>
          <IOSSwitch
            checked={this.state.isFlexible}
            onChange={(e) => {
              const toggled = e.target.checked;
              if (toggled) {
                const preferredMinutes = this.state.preferredTimeMinutes;
                
                // Check if current date is today to apply NOT_BEFORE_H constraint
                const isToday = this.state.sessionDate && this.state.sessionDate.isSame(moment(), "day");
                const minAllowedMinutes = isToday 
                  ? moment().add(NOT_BEFORE_H, "hours").hours() * 60 + moment().add(NOT_BEFORE_H, "hours").minutes()
                  : 480; // 8:00 AM for non-today dates
                
                const clampedStart = Math.max(Math.max(minAllowedMinutes, this.state.minMinutes), preferredMinutes - 120);
                const clampedEnd = Math.min(1320, preferredMinutes + 120);
                this.setState({ isFlexible: true, timeRangeMinutes: [clampedStart, clampedEnd] }, () => {
                  this.validateTimeRange(clampedStart, clampedEnd, preferredMinutes);
                  this.calculateAvailableSlots();
                });
              } else {
                this.setState({ isFlexible: false }, () => {
                  this.calculateAvailableSlots();
                });
              }
            }}
            inputProps={{ "aria-label": "flexible time toggle" }}
          />
        </div>

        {/* Time Range Selector */}
        {this.state.isFlexible && (
          <div className="mt-24 mb-24">
            <div className="position-relative">
              {/* Badge display for time range - matching Figma */}
              <div
                className="d-flex-only justify-content-center align-items-center mb-8"
                style={{
                  gap: "8px",
                  fontSize: "18px",
                  fontWeight: 500,
                  lineHeight: "24px",
                  textAlign: "center",
                  color: "#000000",
                }}
              >
                {moment({
                  hour: Math.floor(this.state.timeRangeMinutes[0] / 60),
                  minute: this.state.timeRangeMinutes[0] % 60,
                }).format("h:mm a")}{" "}
                -{" "}
                {moment({
                  hour: Math.floor(this.state.timeRangeMinutes[1] / 60),
                  minute: this.state.timeRangeMinutes[1] % 60,
                }).format("h:mm a")}
              </div>

              {/* Slider with time labels on sides */}
              <div
                className="d-flex-only justify-content-center align-items-center"
                style={{ gap: "12px" }}
              >
                <div
                  style={{
                    fontFamily: "Poppins",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: "#757575",
                    minWidth: "60px",
                    textAlign: "right",
                  }}
                >
                  8:00 am
                </div>
                <div style={{ flex: 1, position: "relative" }}>
                  <RangeSlider
                    minimumValue={(() => {
                      const isToday = this.state.sessionDate && this.state.sessionDate.isSame(moment(), "day");
                      const minAllowedMinutes = isToday 
                        ? moment().add(NOT_BEFORE_H, "hours").hours() * 60 + moment().add(NOT_BEFORE_H, "hours").minutes()
                        : 480;
                      return Math.max(minAllowedMinutes, this.state.minMinutes);
                    })()}
                    maximumValue={1380}
                    stepSize={15}
                    value={this.state.timeRangeMinutes}
                    onChange={this.handleRangeChange}
                    valueLabelDisplay="off"
                    valueLabelFormat={(val) =>
                      moment({
                        hour: Math.floor(val / 60),
                        minute: val % 60,
                      }).format("h:mm a")
                    }
                    marks={[]}
                    preferredTimeMinutes={(() => {
                      const isToday = this.state.sessionDate && this.state.sessionDate.isSame(moment(), "day");
                      const minAllowedMinutes = isToday 
                        ? moment().add(NOT_BEFORE_H, "hours").hours() * 60 + moment().add(NOT_BEFORE_H, "hours").minutes()
                        : 480;
                      const minVal = Math.max(minAllowedMinutes, this.state.minMinutes);
                      return this.state.preferredTimeMinutes >= minVal &&
                        this.state.preferredTimeMinutes <= 1380
                          ? this.state.preferredTimeMinutes
                          : null;
                    })()}
                  />
                </div>
                <div
                  style={{
                    fontFamily: "Poppins",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: "#757575",
                    minWidth: "60px",
                    textAlign: "left",
                  }}
                >
                  11:00 pm
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Banners - positioned after range picker */}
        {this.renderErrorBanners()}

        {/* Available Times Section */}
        <div className="mb-12">{this.listOptions()}</div>
      </div>
    );
  }
  infoModal() {
    return (
      <LatestModalDesign
        isOpen={this.state.showInfoModal}
        close={() => this.setState({ showInfoModal: false })}
        hideBackArrow
        contentExtraClass="max-width-500"
        applyBtnCopy="Book now"
        disabled={
          !this.state.hasAvailableSlots ||
          (this.state.isFlexible &&
            (this.state.hasError1 || this.state.hasError2))
        }
        apply={() => {
          this.setState({ showInfoModal: false });
          // Trigger the same action as Continue button
          this.submitSelection(
            this.state.sessionTime,
            this.state.formattedList
          );
        }}
        cancelBtnCopy="Close"
      >
        <div className="ptb-24">
          <div className="size-28-36 contentPrimary font-weight-500 mb-16">
            Set your time range
          </div>
          <div className="size-16-24 contentPrimary mb-24">
            Drag the sliders to show the earliest you can start and the latest
            you'd like to finish. This helps us match you with more available
            providers.
            <ul className="mt-16">
              <li>Preferred time must be within the time range.</li>
              <li>
                Time range must give enough time to complete the service, this
                depends on the session length.
              </li>
            </ul>
          </div>
        </div>
      </LatestModalDesign>
    );
  }
  render() {
    return (
      <div className="p-12 mb-12">              
      <div className='contentPrimary size-22-30 medium-font centered mb-8'>Select a time</div>
        {this.responiveDaySelector()}
        {this.hourSelectionSection()}
        {/* CTA Button */}
        <CTAButton
          text={this.state.width > 800 ? "Next: Choose date" : "Continue"}
          disabled={
            !this.state.hasAvailableSlots ||
            (this.state.isFlexible &&
              (this.state.hasError1 || this.state.hasError2))
          }
          action={() => {
            this.submitSelection(
              this.state.sessionTime,
              this.state.formattedList
            );
          }}
          additionalClass="full-width-btn"
        />
        {this.infoModal()}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  booking: state.booking,
  client: state.client,
  bookingFlow: state.bookingFlow,
  fieldsHolder: state.fieldsHolder,
});
export default connect(mapStateToProps, {
  setAvailableTherapistsWithTimeSlots,
})(DateTimeMarketpLace);
